// Screen 1 - "Your app, explained" (docs/design/slice3-knowledge-ux.md §4).
// GET /v1/apps/:appId/knowledge-doc (src/api/knowledge.ts). Renders the four flat
// KnowledgeDoc arrays as four independent sections (§4.1) - no architecture-chain
// diagram (withdrawn, §7: the wire carries no dependency-edge data to draw one from).
import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { isErr } from "@nightcrew/shared";
import type { DataMapEntry, KnowledgeDoc, RankedRisk } from "@nightcrew/shared";
import type { AppDetailContext } from "./AppDetail.js";
import { getKnowledgeDoc, knowledgeErrorKind } from "../api/knowledge.js";
import { isOwnerOrAdmin, useSession } from "../state/session.js";
import glimIllustration from "../assets/glim.svg";

const POLL_MS = 10_000;

const SEVERITY_CLASS: Record<RankedRisk["severity"], "good" | "warn" | "risk"> = {
  low: "good",
  medium: "warn",
  high: "risk",
};
const SEVERITY_LABEL: Record<RankedRisk["severity"], string> = {
  low: "Low priority",
  medium: "Medium priority",
  high: "High priority",
};

const PiiIcon = ({ holds }: { readonly holds: boolean }): React.JSX.Element =>
  holds ? (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
    </svg>
  ) : (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      aria-hidden="true"
    >
      <path d="M5 12h14" />
    </svg>
  );

/**
 * The three "doc not assembled" treatments (§4.3). `failed` was originally built
 * ahead of a live wire signal, per the ux spec's own instruction ("I designed all
 * three UI treatments now so wiring the distinction later is a condition/copy
 * change, not a redesign", §6.1 item 1) - but that caveat is now stale: `failed` IS
 * reachable through the live wire. `knowledgeErrorKind` (`src/api/knowledge.ts:41,51`)
 * recognizes the confirmed `audit_failed` free-string code, and this screen's own
 * effect below (`:195-198`) maps it to `{ status: "not_ready", variant: "failed" }`
 * (Wave-3 seam reconciliation, corrected this fix round). Exported for direct,
 * state-by-state testing regardless.
 */
export const DocNotReadyCard = ({
  variant,
  appName,
  canManage,
  onCheckNow,
}: {
  readonly variant: "building" | "not_started" | "failed";
  readonly appName: string;
  readonly canManage: boolean;
  readonly onCheckNow: () => void;
}): React.JSX.Element => {
  if (variant === "failed") {
    return (
      <div className="card state-card state-failed" data-testid="doc-build-failed">
        <div className="row aic gap8">
          <span className="sev-badge risk" aria-hidden="true">
            !
          </span>
          <div className="h3">The last attempt didn&apos;t finish</div>
        </div>
        <p className="muted">
          The last attempt to build {appName}&apos;s knowledge doc didn&apos;t finish. We&apos;ll
          try again, or you can ask now.
        </p>
        {canManage ? (
          <button type="button" className="btn btn-primary" onClick={onCheckNow}>
            Try again
          </button>
        ) : null}
      </div>
    );
  }
  if (variant === "not_started") {
    return (
      <div className="card state-card state-not-started" data-testid="doc-not-started">
        <div className="row aic gap8">
          <span className="status-chip neutral" aria-hidden="true">
            —
          </span>
          <div className="h3">Not built yet</div>
        </div>
        <p className="muted">We haven&apos;t built {appName}&apos;s knowledge doc yet.</p>
        {canManage ? (
          <button type="button" className="btn btn-primary" onClick={onCheckNow}>
            Build the knowledge doc now
          </button>
        ) : null}
      </div>
    );
  }
  return (
    <div className="card state-card" data-testid="doc-building">
      <div className="glim-badge">
        <img src={glimIllustration} alt="" width={26} height={20} />
      </div>
      <p className="muted">
        Your crew is getting to know {appName}. A first pass is usually ready within a few
        minutes; the full picture fills in on tonight&apos;s shift.
      </p>
      <button type="button" className="btn btn-ghost btn-sm" onClick={onCheckNow}>
        Check now
      </button>
    </div>
  );
};

type DocState =
  | { readonly status: "loading" }
  | { readonly status: "not_ready"; readonly variant: "building" | "not_started" | "failed" }
  | { readonly status: "error" }
  | { readonly status: "success"; readonly doc: KnowledgeDoc };

const DataMapTable = ({
  entries,
  appName,
}: {
  readonly entries: readonly DataMapEntry[];
  readonly appName: string;
}): React.JSX.Element => {
  if (entries.length === 0) {
    return <p className="muted">No data entities found for {appName}.</p>;
  }
  return (
    // W7 fix (design-review): the table<->card toggle now keys off this wrapper's own
    // container-query width (app.css .datamap-container), not the viewport - at
    // laptop the real column here is ~430px even though the viewport clears 640px, so
    // a viewport media query alone rendered a cramped, wrapping table.
    <div className="datamap-container">
      <div className="datamap-table-wrap">
        <table className="tbl mt8">
          <thead>
            <tr>
              <th scope="col">Holds</th>
              <th scope="col">Contains</th>
              <th scope="col">Personal data</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, index) => (
              <tr key={entry.nodeId ?? `${entry.entity}-${String(index)}`}>
                <td>{entry.entity}</td>
                <td>{entry.summary}</td>
                <td>
                  <span className={`status-chip ${entry.holdsPii ? "warn" : "good"}`}>
                    <PiiIcon holds={entry.holdsPii} />
                    {entry.holdsPii ? "Yes" : "No"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="datamap-cards mt8">
        {entries.map((entry, index) => (
          <dl className="card datamap-card" key={entry.nodeId ?? `${entry.entity}-${String(index)}`}>
            <dt className="sr-only">Holds</dt>
            <dd style={{ fontWeight: 600 }}>{entry.entity}</dd>
            <dt>Personal data</dt>
            <dd>
              <span className={`status-chip ${entry.holdsPii ? "warn" : "good"}`}>
                <PiiIcon holds={entry.holdsPii} />
                {entry.holdsPii ? "Yes" : "No"}
              </span>
            </dd>
            <dt>Contains</dt>
            <dd>{entry.summary}</dd>
          </dl>
        ))}
      </div>
    </div>
  );
};

export const AppExplained = (): React.JSX.Element => {
  const { appId, app } = useOutletContext<AppDetailContext>();
  const { state: session } = useSession();
  const canManage = isOwnerOrAdmin(session);
  const [docState, setDocState] = useState<DocState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const cancelled = { current: false };
    void (async () => {
      const result = await getKnowledgeDoc(appId);
      if (cancelled.current) return;
      if (isErr(result)) {
        const kind = knowledgeErrorKind(result.error);
        // Doc-lifecycle codes are now CONFIRMED wire signals (apps/api emits
        // "building" and "audit_failed"; Wave-3 seam reconciliation) - they replace
        // the interim routeConfirmedAt heuristic for those two states.
        if (kind === "building") {
          setDocState({ status: "not_ready", variant: "building" });
          return;
        }
        if (kind === "audit_failed") {
          setDocState({ status: "not_ready", variant: "failed" });
          return;
        }
        if (kind === "not_found") {
          // After the AppDetail getApp gate, a residual not_found can only mean the
          // audit was never queued for this app (ux spec §4.3 "not started").
          setDocState({ status: "not_ready", variant: "not_started" });
          return;
        }
        setDocState({ status: "error" });
        return;
      }
      setDocState({ status: "success", doc: result.value });
    })();
    return () => {
      cancelled.current = true;
    };
  }, [appId, app.routeConfirmedAt, attempt]);

  // Poll every ~10s while the doc is genuinely still building (§4.3) - not while
  // "not started", which has no audit in flight to wait on.
  useEffect(() => {
    if (docState.status !== "not_ready" || docState.variant !== "building") return;
    const id = setInterval(() => {
      setAttempt((a) => a + 1);
    }, POLL_MS);
    return () => { clearInterval(id); };
  }, [docState]);

  const checkNow = (): void => {
    setAttempt((a) => a + 1);
  };

  if (docState.status === "loading") {
    return (
      <div className="wrap">
        <div className="skeleton skeleton-line" style={{ width: "40%" }} />
        <div className="explained-grid mt16">
          <div className="area-features stack gap12">
            <div className="card skeleton-card" />
            <div className="card skeleton-card" />
          </div>
          <div className="area-risks card skeleton-card" />
        </div>
      </div>
    );
  }

  if (docState.status === "not_ready") {
    return (
      <div className="wrap">
        {/* C3 fix (design-review): promoted from .h3 (17px, same visual weight as the
            topbar's own presentational .title) to .h2 (23px, Fraunces) so the page
            heading reads as a real level above the section headings below it, which
            stay at .h3 - restoring the 34/23->17->14 type-scale step. */}
        <h1 className="h2">{app.name}, explained</h1>
        <div className="mt16">
          <DocNotReadyCard
            variant={docState.variant}
            appName={app.name}
            canManage={canManage}
            onCheckNow={checkNow}
          />
        </div>
      </div>
    );
  }

  if (docState.status === "error") {
    return (
      <div className="wrap">
        <h1 className="h2">{app.name}, explained</h1>
        <div className="card state-card mt16">
          <p>We could not load {app.name}&apos;s knowledge doc. Try again.</p>
          <button type="button" className="btn btn-primary" onClick={checkNow}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { doc } = docState;

  return (
    <div className="wrap">
      <h1 className="h2">{app.name}, explained</h1>
      {/* C2 fix (design-review): Features, Dependencies, and Data-map are now three
          independent grid children (each carrying its own area-* class) instead of
          being bundled into one .area-features wrapper - .area-dependencies and
          .area-datamap (app.css .explained-grid) were never claimed before this
          fix, so their rows collapsed empty and Risks rendered after all three
          left-column cards on the mobile stack instead of promoted to position 2
          (ux spec §4.4). */}
      <div className="explained-grid mt16">
        <section className="area-features card pad" aria-labelledby="features-heading">
          <h2 className="h3" id="features-heading">
            Features
          </h2>
          {doc.features.length === 0 ? (
            <p className="muted mt8">No features identified yet.</p>
          ) : (
            <ul className="stack gap12 mt8" style={{ margin: 0, padding: 0, listStyle: "none" }}>
              {doc.features.map((feature, index) => (
                <li key={feature.nodeId ?? `${feature.label}-${String(index)}`}>
                  <b style={{ fontSize: 14 }}>{feature.label}</b>
                  <p className="muted prose-measure" style={{ margin: 0, fontSize: 13.5 }}>
                    {feature.summary}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="area-risks card pad" aria-labelledby="risks-heading">
          <div className="row between aic">
            <h2 className="h3" id="risks-heading">
              Top risks, ranked
            </h2>
            <span className="chip">{doc.rankedRisks.length} found</span>
          </div>
          {doc.rankedRisks.length === 0 ? (
            <div className="well pad mt12">
              <div className="row aic gap8">
                <span className="status-chip good" aria-hidden="true">
                  ✓
                </span>
                <b style={{ fontSize: 12.5 }}>
                  No risks found this pass. The crew will keep checking on future shifts.
                </b>
              </div>
            </div>
          ) : (
            <div className="mt8">
              {doc.rankedRisks.map((risk, index) => (
                <div className="risk-row" key={risk.nodeId ?? `${risk.label}-${String(index)}`}>
                  <span
                    className={`sev-badge ${SEVERITY_CLASS[risk.severity]}`}
                    aria-hidden="true"
                  >
                    {index + 1}
                  </span>
                  <div className="grow">
                    <b style={{ fontSize: 13.5 }}>{risk.label}</b>
                    <div className="muted" style={{ fontSize: 11.5, fontWeight: 600 }}>
                      {SEVERITY_LABEL[risk.severity]}
                    </div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {risk.summary}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="area-dependencies card pad" aria-labelledby="dependencies-heading">
          <h2 className="h3" id="dependencies-heading">
            Dependencies
          </h2>
          {doc.dependencies.length === 0 ? (
            <p className="muted mt8">No dependencies detected.</p>
          ) : (
            <ul className="stack gap12 mt8" style={{ margin: 0, padding: 0, listStyle: "none" }}>
              {doc.dependencies.map((dependency, index) => (
                <li key={dependency.nodeId ?? `${dependency.name}-${String(index)}`}>
                  <b style={{ fontSize: 14 }}>{dependency.name}</b>
                  <p className="muted prose-measure" style={{ margin: 0, fontSize: 13.5 }}>
                    {dependency.summary}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="area-datamap card pad" aria-labelledby="datamap-heading">
          <h2 className="h3" id="datamap-heading">
            The data it holds
          </h2>
          <DataMapTable entries={doc.dataMap} appName={app.name} />
        </section>
      </div>
    </div>
  );
};
